import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { clearDraft, isEmptyDraft, loadDraft, saveDraft } from "../lib/drafts";
import { useApiErrorHandler } from "../lib/session";
import type { ActionResult, VisitState } from "../lib/types";
import { AudioRecorder } from "../components/AudioRecorder";
import { Notice, categoryLabel, evidenceLabel } from "../components/bits";

/**
 * "complete" and "fail" are the two verdicts on a card. Only a Herausforderung
 * offers both; every other card has one way to resolve, and abwerfen is what you
 * do with a task you did not manage.
 */
type Action = "complete" | "fail" | "discard" | "hold" | "veto";

export default function CardDetail({
  visit,
  setVisit,
  holdSize,
  onConquest,
}: {
  visit: VisitState | null;
  setVisit: (visit: VisitState | null) => void;
  holdSize: number;
  onConquest: (result: ActionResult, visitEnded: boolean) => void;
}) {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const handleError = useApiErrorHandler();

  const [photos, setPhotos] = useState<File[]>([]);
  const [audio, setAudio] = useState<Blob | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<null | Action>(null);
  const [error, setError] = useState<string | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const card = useMemo(() => {
    if (!visit) return null;
    return visit.hand.find((c) => c.id === Number(cardId)) ?? visit.hold.find((c) => c.id === Number(cardId)) ?? null;
  }, [visit, cardId]);

  const previews = useMemo(() => photos.map((file) => URL.createObjectURL(file)), [photos]);
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [previews]);

  // Which card the fields below currently hold. Until the draft for *this* card has
  // been read back, saving would write the previous card's photos under this card's
  // id -- the effects both run on the same commit, and this ref is what orders them.
  const draftFor = useRef<number | null>(null);

  useEffect(() => {
    const id = Number(cardId);
    let active = true;
    draftFor.current = null;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reihenfolge mit draftFor schützt Entwürfe, siehe Kommentar oben
    setPhotos([]);
    setAudio(null);
    setNote("");
    // Ohne lesbaren Entwurf geht es mit leeren Feldern weiter (wie bisher)
    void loadDraft(id).then((draft) => {
      if (!active) return;
      if (draft) {
        // Merged rather than assigned. Opening the store takes a moment the first
        // time, and a photo taken inside that window is already in state -- so the
        // stored draft goes in front of it instead of replacing it.
        setPhotos((added) => [...draft.photos, ...added]);
        setAudio((added) => added ?? draft.audio);
        setNote((typed) => typed || draft.note);
      }
      draftFor.current = id;
    });
    return () => {
      active = false;
    };
  }, [cardId]);

  // The latest values, read at write time rather than captured per render, so a
  // debounced write can never land with a stale photo list behind it.
  const latest = useRef({ photos, audio, note });
  // eslint-disable-next-line react-hooks/refs -- gewollt: die neuesten Werte, beim Schreiben gelesen (siehe oben)
  latest.current = { photos, audio, note };

  // Set once an action has succeeded, so the unmount flush below does not write a
  // draft back after it has just been cleared.
  const resolved = useRef(false);

  // Photos and audio are written the moment they change. They are the expensive
  // things to lose and they change a handful of times, so there is nothing to gain
  // by deferring them.
  useEffect(() => {
    const id = Number(cardId);
    // Before the load has landed, an empty draft has nothing to lose and must not
    // be written -- it would clear the very draft still being read back. Anything
    // the player has actually added is written straight away; the load merges
    // around it. Waiting for the load here is what used to drop a quick photo.
    if (draftFor.current !== id && isEmptyDraft(latest.current)) return;
    void saveDraft(id, latest.current);
  }, [cardId, photos, audio]);

  // The note is debounced -- one write per keystroke would re-put the photos with
  // it -- and flushed below, so leaving the screen mid-word still keeps the word.
  useEffect(() => {
    const id = Number(cardId);
    if (draftFor.current !== id && isEmptyDraft(latest.current)) return;
    const timer = window.setTimeout(() => void saveDraft(id, latest.current), 400);
    return () => window.clearTimeout(timer);
  }, [cardId, note]);

  // Leaving the card, or switching to another one, flushes whatever the debounce
  // was still holding. This is the case the drafts exist for: a photo taken and
  // then a tap straight back to the hand.
  useEffect(() => {
    const id = Number(cardId);
    return () => {
      if (resolved.current) return;
      // Clearing (an empty draft) is only correct once the stored one has been read
      // back; writing something the player collected is always correct.
      if (draftFor.current === id || !isEmptyDraft(latest.current)) {
        void saveDraft(id, latest.current);
      }
    };
  }, [cardId]);

  // The card is gone -- resolved in another tab, or the visit ended. Go back rather
  // than showing a broken screen.
  useEffect(() => {
    if (visit && !card) navigate("/", { replace: true });
  }, [visit, card, navigate]);

  if (!visit || !card) return null;

  const isHeld = card.status === "held";
  const vetoSpent = Boolean(visit.place.category_veto);
  const canVeto = !vetoSpent && !visit.place.conquered;
  const isChallenge = card.category === "CHALLENGE";
  const stake = card.failure_penalty;

  async function act(action: Action) {
    if (!card) return;
    if (action === "fail") {
      const message = visit!.place.conquered
        ? "Als nicht geschafft eintragen? Das lässt sich nicht zurücknehmen."
        : `Als nicht geschafft eintragen? Das Ziel für ${visit!.place.name} steigt um ${card.failure_penalty} auf ${
            visit!.place.target_points + card.failure_penalty
          }, und zurücknehmen lässt es sich nicht.`;
      if (!confirm(message)) return;
    }
    if (action === "discard") {
      const message = visit!.place.conquered
        ? `Diese Karte abwerfen? In ${visit!.place.name} wird sie nicht noch einmal ausgeteilt.`
        : `Diese Karte abwerfen? In ${visit!.place.name} wird sie nicht noch einmal ausgeteilt, und das Ziel steigt um ${visit!.discard_penalty} auf ${
            visit!.place.target_points + visit!.discard_penalty
          }.`;
      if (!confirm(message)) return;
    }
    if (action === "veto") {
      const label = categoryLabel(card.category);
      if (
        !confirm(
          `${label} in ${visit!.place.name} streichen? Diese Kategorie wird hier nicht mehr ausgeteilt. Das geht nur einmal pro Ort, und das Ziel steigt um ${visit!.category_veto_penalty} auf ${visit!.place.target_points + visit!.category_veto_penalty}.`,
        )
      ) {
        return;
      }
    }

    setBusy(action);
    setError(null);
    try {
      const result =
        action === "complete" || action === "fail"
          ? await api.completeCard(
              card.id,
              { photos, audio, note },
              action === "fail" ? "failure" : "success",
            )
          : action === "discard"
            ? await api.discardCard(card.id)
            : action === "hold"
              ? await api.holdCard(card.id)
              : await api.vetoCategory(card.id);

      // Holding keeps the card in play, so its draft stays with it. Everything
      // else resolves the card, and a resolved card's draft has been sent.
      if (action !== "hold") {
        resolved.current = true;
        await clearDraft(card.id);
      }

      if (result.conquered_now) {
        // The place is taken, so this visit is over. Ending it here means Jetzt
        // comes back as "Wo bist du?" rather than offering a hand that can no
        // longer earn anything. The cards park at the place like any ended visit.
        //
        // Best effort: if the call does not get through, the visit simply stays
        // open and the manual "Besuch beenden" button still works. Losing the
        // conquest moment over a failed housekeeping request would be worse.
        let ended = true;
        try {
          await api.endVisit(result.visit_id);
          setVisit(null);
        } catch {
          ended = false;
          setVisit(result);
        }
        onConquest(result, ended);
        navigate("/conquest", { replace: true });
      } else {
        setVisit(result);
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(handleError(err));
      setBusy(null);
    }
  }

  const wantsPhoto = card.evidence === "photo";
  const wantsAudio = card.evidence === "audio";

  function addPhotos(fileList: FileList | null) {
    // Snapshot before the input is reset: FileList is live, so clearing the value
    // would otherwise drop the selection before React applies the state update.
    const added = Array.from(fileList ?? []);
    if (added.length === 0) return;
    setPhotos((existing) => [...existing, ...added]);
  }

  return (
    <div className="screen">
      <button className="btn btn--ghost" onClick={() => navigate(-1)} style={{ marginLeft: -12 }}>
        ← Zurück
      </button>

      <div className={`cat-${card.category}`} style={{ marginTop: 8 }}>
        <div className="row" style={{ gap: 10, marginBottom: 12 }}>
          <span className="card__category">{categoryLabel(card.category)}</span>
          <span className="card__points" style={{ marginLeft: "auto" }}>
            {card.points} Pkt
          </span>
        </div>

        <p
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.6rem",
            lineHeight: 1.34,
            margin: "0 0 6px",
            fontWeight: 500,
          }}
        >
          {card.text}
        </p>
        <p className="tiny muted">{evidenceLabel(card.evidence)}</p>
        {isChallenge ? (
          <p className="tiny" style={{ color: "var(--cat)", fontWeight: 700, margin: "6px 0 0" }}>
            Wette: geschafft bringt {card.points} Punkte, nicht geschafft hebt das Ziel um{" "}
            {stake} an.
          </p>
        ) : null}
      </div>

      <hr style={{ border: 0, borderTop: "1px solid var(--rule)", margin: "22px 0" }} />

      <div className="stack">
        {wantsPhoto || photos.length > 0 ? (
          <div className="stack">
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              onChange={(e) => {
                addPhotos(e.target.files);
                e.target.value = "";
              }}
            />
            <button
              className="btn btn--quiet btn--block"
              onClick={() => cameraRef.current?.click()}
              type="button"
            >
              {photos.length ? "Noch eins aufnehmen" : "Foto aufnehmen"}
            </button>

            {previews.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
                {previews.map((url, index) => (
                  <div key={url} style={{ position: "relative" }}>
                    <img
                      src={url}
                      alt=""
                      style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 10, display: "block" }}
                    />
                    <button
                      type="button"
                      aria-label="Foto entfernen"
                      onClick={() => setPhotos((p) => p.filter((_, i) => i !== index))}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        border: 0,
                        background: "rgb(0 0 0 / 62%)",
                        color: "#fff",
                        lineHeight: 1,
                        fontSize: 15,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {wantsAudio ? <AudioRecorder value={audio} onChange={setAudio} /> : null}

        <div className="field">
          <label htmlFor="note">{card.evidence === "note" ? "Deine Notiz" : "Notiz (optional)"}</label>
          <textarea
            id="note"
            className="textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={card.evidence === "note" ? "Schreib auf, was passiert ist." : "Alles, was erinnernswert ist."}
          />
        </div>

        {error ? <Notice tone="error">{error}</Notice> : null}

        <button className="btn btn--lg btn--block" onClick={() => void act("complete")} disabled={busy !== null}>
          {busy === "complete" ? <span className="spin" aria-hidden /> : null}
          {busy === "complete"
            ? "Wird gespeichert"
            : isChallenge
              ? `Geschafft · +${card.points} Pkt`
              : `Erledigt · +${card.points} Pkt`}
        </button>

        {/* A lost bet is still a played card: it keeps the note and draws a
            replacement. It is not abwerfen, so it does not sit with it. */}
        {isChallenge ? (
          <button
            className="btn btn--quiet btn--lg btn--block"
            onClick={() => void act("fail")}
            disabled={busy !== null}
          >
            {busy === "fail" ? <span className="spin" aria-hidden /> : null}
            {busy === "fail"
              ? "Wird gespeichert"
              : visit.place.conquered
                ? "Nicht geschafft"
                : `Nicht geschafft · Ziel +${stake}`}
          </button>
        ) : null}

        {!isHeld ? (
          <button
            className="btn btn--quiet btn--lg btn--block"
            onClick={() => void act("hold")}
            disabled={busy !== null || visit.hold.length >= holdSize}
            title={
              visit.hold.length >= holdSize ? "Es liegen schon zwei Karten zurück" : undefined
            }
          >
            Zurücklegen
          </button>
        ) : null}

        <div className="row" style={{ gap: 10 }}>
          <button
            className="btn btn--danger"
            style={{ flex: 1 }}
            onClick={() => void act("veto")}
            disabled={busy !== null || !canVeto}
            title={
              vetoSpent
                ? `${categoryLabel(visit.place.category_veto!)} ist in diesem Ort schon gestrichen`
                : visit.place.conquered
                  ? "Ein Kategorie-Veto gibt es nur während der Eroberung"
                  : `${categoryLabel(card.category)} in ${visit.place.name} nicht mehr ausgeteilt`
            }
          >
            Kategorie-Veto
          </button>
          <button className="btn btn--danger" style={{ flex: 1 }} onClick={() => void act("discard")} disabled={busy !== null}>
            Abwerfen
          </button>
        </div>

        <p className="tiny muted" style={{ textAlign: "center", margin: 0 }}>
          {isChallenge ? (
            <>
              Eine Herausforderung wird ehrlich eingetragen, nicht bestanden oder nicht — beides
              zählt als gespielt, und beides behält, was du hier aufschreibst.{" "}
            </>
          ) : null}
          {!visit.place.conquered ? (
            <>
              Abwerfen hebt das Ziel für {visit.place.name} um {visit.discard_penalty} an. Es
              nimmt dir nie Punkte weg, die du schon verdient hast.{" "}
            </>
          ) : null}
          Eine abgeworfene Karte kommt in {visit.place.name} nicht zurück.
          {canVeto ? (
            <> Ein Kategorie-Veto streicht eine ganze Kategorie hier — einmal, und hebt das Ziel um {visit.category_veto_penalty} an.</>
          ) : vetoSpent ? (
            <> {categoryLabel(visit.place.category_veto!)} wird in {visit.place.name} nicht mehr ausgeteilt.</>
          ) : null}
        </p>
      </div>
    </div>
  );
}
